import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import './index.css';
import './styles/design-tokens.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { preloadCommonImages } from './utils/imagePreloader';
import { AuthErrorProvider } from './contexts/AuthErrorContext';
import { CurrencyVisibilityProvider } from './contexts/CurrencyVisibilityContext';

// 401 에러를 감지하는 함수
function is401Error(error: unknown): boolean {
  if (!error) return false;

  // Error 객체인 경우
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();
    if (errorMessage.includes('unauthorized') || errorMessage.includes('401')) {
      return true;
    }
  }

  // Supabase 에러 객체인 경우
  const errorAny = error as {
    status?: number;
    statusCode?: number;
    context?: { status?: number } | Response;
    message?: string;
  };

  // status 코드 확인
  if (errorAny.status === 401 || errorAny.statusCode === 401) {
    return true;
  }

  // context에서 status 확인
  if (errorAny.context) {
    if (errorAny.context instanceof Response) {
      if (errorAny.context.status === 401) {
        return true;
      }
    } else if (
      typeof errorAny.context === 'object' &&
      'status' in errorAny.context &&
      errorAny.context.status === 401
    ) {
      return true;
    }
  }

  return false;
}

// QueryCache와 MutationCache 생성
const queryCache = new QueryCache({
  onError: (error) => {
    if (is401Error(error)) {
      // 401 에러 발생 시 전역 이벤트 발생
      window.dispatchEvent(new CustomEvent('auth-error-401'));
    }
  },
});

const mutationCache = new MutationCache({
  onError: (error) => {
    if (is401Error(error)) {
      // 401 에러 발생 시 전역 이벤트 발생
      window.dispatchEvent(new CustomEvent('auth-error-401'));
    }
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
});

// 앱 시작 시 공통 이미지 프리로드
preloadCommonImages();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthErrorProvider>
        <CurrencyVisibilityProvider>
          <App />
        </CurrencyVisibilityProvider>
      </AuthErrorProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

reportWebVitals();
